import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { staggerContainer, staggerItem } from "@/lib/motion";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";

const SEV_LABEL = { critical: "Critical", high: "High", medium: "Medium", low: "Low", info: "Info" };
const ENGINE_LABEL = { authorization: "Authorization", su24: "SU24", odata: "OData", correlation: "Correlation" };

/** Real severity × engine cross-tab. Every cell is a live finding count and a
 * shortcut into the filtered Findings centre. */
function DistributionMatrix({ matrix, onCell }) {
    const { severities = [], engines = [], cells = {}, max = 0, total = 0 } = matrix || {};
    if (!severities.length) return null;
    return (
        <div data-testid="risk-distribution-matrix">
            <div
                className="grid gap-1.5 text-[11px] font-mono"
                style={{ gridTemplateColumns: `96px repeat(${engines.length}, minmax(0,1fr)) 72px` }}
            >
                <div />
                {engines.map((e) => (
                    <div key={e} className="text-center text-slate-500 uppercase tracking-wider pb-1">
                        {ENGINE_LABEL[e] || e}
                    </div>
                ))}
                <div className="text-center text-slate-500 uppercase tracking-wider pb-1">Total</div>

                {severities.map((sev) => {
                    const rowTotal = engines.reduce((s, e) => s + (cells[sev]?.[e] || 0), 0);
                    return (
                        <React.Fragment key={sev}>
                            <div className={`flex items-center gap-2 sev-${sev} pr-2`}>
                                <span className="dot" />
                                <span className="uppercase tracking-wider text-[10px]">{SEV_LABEL[sev] || sev}</span>
                            </div>
                            {engines.map((e) => {
                                const n = cells[sev]?.[e] || 0;
                                const t = max ? n / max : 0;
                                return (
                                    <button
                                        key={e}
                                        data-testid={`risk-matrix-cell-${sev}-${e}`}
                                        disabled={!n}
                                        onClick={() => n && onCell(sev, e)}
                                        title={n ? `${n} ${SEV_LABEL[sev]} finding(s) from ${ENGINE_LABEL[e] || e} — open in Findings` : "No findings"}
                                        className={`h-11 rounded-[3px] border flex items-center justify-center transition-all duration-300 ${
                                            n
                                                ? "border-white/10 text-white hover:border-[rgb(var(--accent-1-rgb) / 0.55)] hover:-translate-y-0.5 cursor-pointer"
                                                : "border-white/5 text-slate-700 cursor-default"
                                        }`}
                                        style={n ? { background: `rgb(var(--accent-1-rgb) / ${0.08 + t * 0.42})` } : undefined}
                                    >
                                        {n || "—"}
                                    </button>
                                );
                            })}
                            <div className="h-11 rounded-[3px] border border-white/5 bg-white/[0.02] flex items-center justify-center text-slate-300">
                                {rowTotal || "—"}
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>
            <div className="text-[10px] font-mono text-slate-500 mt-3 uppercase tracking-wider">
                {total} finding{total === 1 ? "" : "s"} · severity × detection engine · click a cell to inspect
            </div>
        </div>
    );
}

export default function Risk() {
    const [workspaces, setWorkspaces] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const nav = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const [ws, ov] = await Promise.all([api.get("/workspaces"), api.get("/stats/overview")]);
                setWorkspaces(ws.data);
                setStats(ov.data);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const data = useMemo(
        () =>
            [...workspaces]
                .sort((a, b) => (b.risk_score || 0) - (a.risk_score || 0))
                .slice(0, 12)
                .map((w) => ({ id: w.id, name: w.name.length > 26 ? `${w.name.slice(0, 25)}…` : w.name, risk: w.risk_score || 0 })),
        [workspaces],
    );

    return (
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
            <motion.div variants={staggerItem}>
                <Panel eyebrow="Risk Posture" title="Workspaces by Risk Score" testId="risk-posture-panel">
                    {data.length === 0 ? (
                        <div className="py-10 text-center text-sm text-slate-500" data-testid="risk-empty-state">
                            {loading ? "Reading posture…" : "No workspaces yet — create one and run an analysis to see risk scores."}
                        </div>
                    ) : (
                        <ResponsiveContainer minWidth={1} minHeight={1} width="100%" height={Math.max(160, data.length * 46 + 60)}>
                            <BarChart data={data} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.07)" horizontal={false} />
                                <XAxis type="number" domain={[0, 100]} allowDecimals={false} stroke="#94a3b8" tick={{ fontSize: 10 }} />
                                <YAxis
                                    dataKey="name"
                                    type="category"
                                    stroke="#94A3B8"
                                    tick={{ fontSize: 11, fontFamily: "IBM Plex Sans" }}
                                    width={190}
                                />
                                <Tooltip
                                    cursor={{ fill: "rgba(255,255,255,0.03)" }}
                                    contentStyle={{ background: "var(--bg-elevated)", border: "1px solid rgb(var(--accent-1-rgb) / 0.25)" }}
                                />
                                <Bar dataKey="risk" radius={[0, 3, 3, 0]} maxBarSize={26} cursor="pointer" onClick={(d) => d?.id && nav(`/workspaces/${d.id}`)}>
                                    {data.map((d) => (
                                        <Cell
                                            key={d.id}
                                            fill={d.risk > 60 ? "var(--neon-red)" : d.risk > 30 ? "var(--warning)" : "var(--safe)"}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </Panel>
            </motion.div>

            <motion.div variants={staggerItem}>
                <Panel eyebrow="Heatmap" title="Risk Distribution Matrix" testId="risk-matrix-panel">
                    {stats?.severity_engine_matrix?.total ? (
                        <DistributionMatrix
                            matrix={stats.severity_engine_matrix}
                            onCell={(sev, eng) => nav(`/findings?severity=${sev}&engine=${eng}`)}
                        />
                    ) : (
                        <div className="py-10 text-center text-sm text-slate-500" data-testid="risk-matrix-empty">
                            {loading ? "Aggregating findings…" : "No findings recorded yet — run an analysis on a workspace first."}
                        </div>
                    )}
                </Panel>
            </motion.div>
        </motion.div>
    );
}
